import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contabilidadService } from '../services/contabilidadService';
import type { DistribucionUtilidad, ConfirmarRepartoPayload } from '../types';

export const HISTORIAL_UTILIDAD_QUERY_KEY = ['contabilidad', 'historial-utilidad'] as const;

export function useUtilidad() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<DistribucionUtilidad[]>({
    queryKey: HISTORIAL_UTILIDAD_QUERY_KEY,
    queryFn: () => contabilidadService.listHistorialUtilidad(),
    staleTime: 1000 * 60 * 5,
  });

  const confirmarMutation = useMutation({
    mutationFn: (payload: ConfirmarRepartoPayload) =>
      contabilidadService.confirmarReparto(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HISTORIAL_UTILIDAD_QUERY_KEY });
      // Invalida KPIs y movimientos ya que el reparto crea egresos
      queryClient.invalidateQueries({ queryKey: ['contabilidad', 'kpis'] });
      queryClient.invalidateQueries({ queryKey: ['contabilidad', 'movimientos'] });
      queryClient.invalidateQueries({ queryKey: ['contabilidad', 'cajas'] });
    },
  });

  const cancelarMutation = useMutation({
    mutationFn: (id: number) => contabilidadService.cancelarDistribucion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HISTORIAL_UTILIDAD_QUERY_KEY });
      // Invalida KPIs y movimientos ya que cancelar revierte los egresos del reparto
      queryClient.invalidateQueries({ queryKey: ['contabilidad', 'kpis'] });
      queryClient.invalidateQueries({ queryKey: ['contabilidad', 'movimientos'] });
      queryClient.invalidateQueries({ queryKey: ['contabilidad', 'cajas'] });
    },
  });

  return {
    historialUtilidad: data ?? [],
    isLoading,
    error,
    confirmarReparto: confirmarMutation.mutateAsync,
    isConfirmando: confirmarMutation.isPending,
    cancelarDistribucion: cancelarMutation.mutateAsync,
    isCancelando: cancelarMutation.isPending,
  };
}
